import { FileText, Link2, Type } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage } from "../../types";

interface BuilderStaticPageSettingsProps {
  page: ProposalDocumentLayoutPage;
  readOnly?: boolean;
  onChange: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  className?: string;
}

type StaticPageSettings = Record<string, string | boolean | number | null>;

function getSettings(page: ProposalDocumentLayoutPage): StaticPageSettings {
  return page.settings && typeof page.settings === "object"
    ? (page.settings as StaticPageSettings)
    : {};
}

function getStringSetting(
  settings: StaticPageSettings,
  key: string,
  fallback = ""
) {
  const value = settings[key];
  return typeof value === "string" ? value : fallback;
}

function getBooleanSetting(
  settings: StaticPageSettings,
  key: string,
  fallback: boolean
) {
  const value = settings[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateSetting(
  settings: StaticPageSettings,
  key: string,
  value: string | boolean | number | null
) {
  return {
    settings: {
      ...settings,
      [key]: value,
    },
  };
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  icon,
  hint,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {icon}
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />

      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

export function BuilderStaticPageSettings({
  page,
  readOnly = false,
  onChange,
  className,
}: BuilderStaticPageSettingsProps) {
  const settings = getSettings(page);

  const title = getStringSetting(settings, "title", page.title ?? "Static Page");
  const sourceType = getStringSetting(settings, "sourceType", "brand_static_page");
  const sourceId = getStringSetting(settings, "sourceId");
  const customIntro = getStringSetting(settings, "customIntro");
  const showTitle = getBooleanSetting(settings, "showTitle", true);
  const allowPerProposalOverrides = getBooleanSetting(
    settings,
    "allowPerProposalOverrides",
    true
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-amber-500/10 via-orange-500/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <FileText className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              Static Page Settings
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Adjust how this reusable page is framed, where its content comes
              from, and how flexible it stays at the proposal level.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Page framing"
          description="Control how this static page is introduced when it appears in the proposal."
        >
          <div>
            <FieldLabel
              label="Display title"
              icon={<Type className="h-4 w-4" />}
              hint="Visible title used when this static page is rendered."
            />
            <input
              value={title}
              onChange={(e) =>
                onChange(updateSetting(settings, "title", e.target.value))
              }
              disabled={readOnly}
              placeholder="Static Page"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Intro / lead-in"
              hint="Optional text shown before the reusable page content."
            />
            <textarea
              rows={4}
              value={customIntro}
              onChange={(e) =>
                onChange(updateSetting(settings, "customIntro", e.target.value))
              }
              disabled={readOnly}
              placeholder="Optional intro text shown before the static page content"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Reusable source"
          description="Map this page back to the reusable content source it should render from."
        >
          <div>
            <FieldLabel
              label="Source type"
              icon={<Link2 className="h-4 w-4" />}
              hint="Choose whether the content comes from Brand Setup, the template, or a one-off page."
            />
            <select
              value={sourceType}
              onChange={(e) =>
                onChange(updateSetting(settings, "sourceType", e.target.value))
              }
              disabled={readOnly}
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            >
              <option value="brand_static_page" className="bg-[#111]">
                Brand Setup static page
              </option>
              <option value="template_static_page" className="bg-[#111]">
                Template-linked static page
              </option>
              <option value="custom" className="bg-[#111]">
                Custom per-page content
              </option>
            </select>
          </div>

          <div>
            <FieldLabel
              label="Source ID or key"
              hint="Use the reusable page id or the source key this page should resolve."
            />
            <input
              value={sourceId}
              onChange={(e) =>
                onChange(updateSetting(settings, "sourceId", e.target.value))
              }
              disabled={readOnly}
              placeholder="static-page-id or source key"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
            <p className="text-xs leading-5 text-white/40">
              Use Brand Setup for shared reusable pages, Template-linked when the
              source belongs to this template flow, or Custom when the page should
              stand on its own.
            </p>
          </div>
        </SectionCard>

        <SectionCard
          title="Page behavior"
          description="Choose how much framing and proposal-level flexibility this static page allows."
        >
          <ToggleField
            label="Show page title"
            description="Display the page title above the static page content."
            checked={showTitle}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showTitle", checked))
            }
          />

          <ToggleField
            label="Allow per-proposal overrides"
            description="Let proposal-level edits customize this page without changing the reusable source page."
            checked={allowPerProposalOverrides}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(
                updateSetting(settings, "allowPerProposalOverrides", checked)
              )
            }
          />
        </SectionCard>
      </div>
    </div>
  );
}